import { defineFunctionIfMissing } from "./backport-utils";

/**
 * Polyfill for r72 THREE.Triangle
 *
 * Triangle (r180): https://github.com/mrdoob/three.js/blob/r180/src/math/Triangle.js
 */
export const defineTriangle = (THREE) => {

  const _vab = new THREE.Vector3();
  const _vac = new THREE.Vector3();
  const _vbc = new THREE.Vector3();
  const _vap = new THREE.Vector3();
  const _vbp = new THREE.Vector3();
  const _vcp = new THREE.Vector3();

  defineFunctionIfMissing(THREE?.Triangle?.prototype, "getMidpoint", function getMidpoint(target) {
    return target.addVectors(this.a, this.b).add(this.c).multiplyScalar(1 / 3);
  });

  defineFunctionIfMissing(THREE?.Triangle?.prototype, "closestPointToPoint", function closestPointToPoint(p, target) {
    const a = this.a,b = this.b,c = this.c;
    let v, w;

    // algorithm thanks to Real-Time Collision Detection by Christer Ericson,
    // published by Morgan Kaufmann Publishers, (c) 2005 Elsevier Inc.,
    // under the accompanying license; see chapter 5.1.5 for detailed explanation.
    // basically, we're distinguishing which of the voronoi regions of the triangle
    // the point lies in with the minimum amount of redundant computation.

    _vab.subVectors(b, a);
    _vac.subVectors(c, a);
    _vap.subVectors(p, a);
    const d1 = _vab.dot(_vap);
    const d2 = _vac.dot(_vap);
    if (d1 <= 0 && d2 <= 0) {

      // vertex region of A; barycentric coords (1, 0, 0)
      return target.copy(a);

    }

    _vbp.subVectors(p, b);
    const d3 = _vab.dot(_vbp);
    const d4 = _vac.dot(_vbp);
    if (d3 >= 0 && d4 <= d3) {

      // vertex region of B; barycentric coords (0, 1, 0)
      return target.copy(b);

    }

    const vc = d1 * d4 - d3 * d2;
    if (vc <= 0 && d1 >= 0 && d3 <= 0) {

      v = d1 / (d1 - d3);
      // edge region of AB; barycentric coords (1-v, v, 0)
      return target.copy(a).addScaledVector(_vab, v);

    }

    _vcp.subVectors(p, c);
    const d5 = _vab.dot(_vcp);
    const d6 = _vac.dot(_vcp);
    if (d6 >= 0 && d5 <= d6) {

      // vertex region of C; barycentric coords (0, 0, 1)
      return target.copy(c);

    }

    const vb = d5 * d2 - d1 * d6;
    if (vb <= 0 && d2 >= 0 && d6 <= 0) {

      w = d2 / (d2 - d6);
      // edge region of AC; barycentric coords (1-w, 0, w)
      return target.copy(a).addScaledVector(_vac, w);

    }

    const va = d3 * d6 - d5 * d4;
    if (va <= 0 && d4 - d3 >= 0 && d5 - d6 >= 0) {

      _vbc.subVectors(c, b);
      w = (d4 - d3) / (d4 - d3 + (d5 - d6));
      // edge region of BC; barycentric coords (0, 1-w, w)
      return target.copy(b).addScaledVector(_vbc, w); // edge region of BC

    }

    // face region
    const denom = 1 / (va + vb + vc);
    // u = va * denom
    v = vb * denom;
    w = vc * denom;

    return target.copy(a).addScaledVector(_vab, v).addScaledVector(_vac, w);
  });
};